import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { headline2, headline3 } from "@shared/styles";
import Dropdown from "@shared/v2/Dropdown";
import DropdownDatePicker from "@shared/v2/DropdownDatePicker";
import TextInput from "@shared/v2/TextInput";
// eslint-disable-next-line import/no-unresolved
import socialMediaNormalizer, { SOCIAL_MEDIA_CONFIG } from "@shared/socialMediaNormalizer";
import FieldDateV1 from "../../shared/UtilsV1/FieldDateV1";
import TagsSection from "../ModalSections/TagsSection";
import FieldTextWithIconV1 from "../../shared/UtilsV1/FieldTextWithIconV1";
import { containerView } from "./styles";
import { setPersonValue, setSocialMediaError } from "../../reducers/contactsReducer";
import {
  useContactFormOptions,
  useContactPerson,
  useSocialMediaErrors,
} from "../../reducers/contactsReducer/selectors";

const PersonalDetailsView = ({ birthdateError, modalType }) => {
  const dispatch = useDispatch();
  const person = useContactPerson();
  const formOptions = useContactFormOptions();
  const socialMediaErrors = useSocialMediaErrors();
  const { anniversary, birthdate } = person.milestones || {};

  const handleSocialMediaBlur = (config) => {
    const value = person[config.key] || "";
    const result = socialMediaNormalizer({
      input: value,
      domain: config.domain,
      exceptions: config.exceptions || [],
      errorMessages: config.errorMessages,
    });

    if ("error" in result) {
      dispatch(setSocialMediaError({ key: config.key, error: result.error }));
    } else {
      dispatch(setSocialMediaError({ key: config.key, error: null }));
      if (result.success !== null && result.success !== value) {
        dispatch(setPersonValue({ key: config.key, value: result.success }));
      }
    }
  };

  return (
    <div className={containerView}>
      <div className={`tw-text-neutral-gray-75 ${headline2}`}>Personal Details</div>
      {!["lender", "websiteProfile"].includes(modalType) && (
        <>
          <TextInput
            label="Description"
            name="description"
            multiline
            value={person.description}
            onChange={(e) => dispatch(setPersonValue({ key: "description", value: e.target.value }))}
          />

          <TagsSection
            tagOptions={formOptions.tagsOptions}
            tags={person.tagNames}
            setPersonValue={(...args) => dispatch(setPersonValue(...args))}
          />

          <div className="tw-flex tw-gap-[24px]">
            <Dropdown
              containerClassName="tw-flex-1"
              label="Timezone"
              options={formOptions.timezoneOptions}
              value={formOptions.timezoneOptions.find((o) => o.value === person.timezone)}
              onChange={(o) => dispatch(setPersonValue({ key: "timezone", value: o.value }))}
              menuShouldComeToFront
            />
            <DropdownDatePicker
              error={birthdateError}
              label="Birthdate"
              menuShouldComeToFront
              value={birthdate}
              onChange={(value) =>
                dispatch(
                  setPersonValue({
                    key: "milestones.birthdate",
                    value,
                  }),
                )
              }
            />
          </div>

          <div className="tw-flex tw-gap-[24px]">
            <Dropdown
              containerClassName="tw-flex-1"
              label="Marital Status"
              options={formOptions.maritalStatusOptions}
              value={formOptions.maritalStatusOptions.find((o) => o.value === person.maritalStatus)}
              onChange={(o) => dispatch(setPersonValue({ key: "maritalStatus", value: o.value }))}
              menuShouldComeToFront
            />
            <FieldDateV1
              label="Home Anniversary"
              dateFormat="MM/DD/YYYY"
              value={anniversary}
              onChange={(value) =>
                dispatch(
                  setPersonValue({
                    key: "milestones.anniversary",
                    value,
                  }),
                )
              }
            />
          </div>

          <div className="tw-flex tw-gap-[24px]">
            <TextInput
              containerClassName="tw-flex-1"
              label="Kids"
              value={person.kids}
              onChange={(e) => dispatch(setPersonValue({ key: "kids", value: e.target.value }))}
            />
            <TextInput
              containerClassName="tw-flex-1"
              label="Pets"
              value={person.pets}
              onChange={(e) => dispatch(setPersonValue({ key: "pets", value: e.target.value }))}
            />
          </div>
        </>
      )}

      <TextInput
        label="Company"
        value={person.company}
        onChange={(e) => dispatch(setPersonValue({ key: "company", value: e.target.value }))}
      />

      {formOptions.militaryStatusOptions && (
        <div className="tw-flex tw-gap-[24px]">
          <Dropdown
            containerClassName="tw-flex-1"
            label="Military Status"
            options={formOptions.militaryStatusOptions}
            value={formOptions.militaryStatusOptions.find((o) => o.value === person.militaryStatus)}
            onChange={(o) => dispatch(setPersonValue({ key: "militaryStatus", value: o.value }))}
            menuShouldComeToFront
          />
        </div>
      )}

      {modalType !== "lender" && (
        <>
          <hr className="tw-w-full tw-border-gray-10" />
          <div className={headline3}>Social Media</div>
          <div className="tw-text-gray-50">
            Social links are displayed in various places on your Brivity website including the links page,
            agent profile, and page footers.
          </div>
          <div className="tw-flex tw-gap-[24px]">
            <FieldTextWithIconV1
              label="Facebook"
              iconClassName={SOCIAL_MEDIA_CONFIG[0].iconClassName}
              value={person.facebook}
              onChange={(value) => dispatch(setPersonValue({ key: "facebook", value }))}
              onBlur={() => handleSocialMediaBlur(SOCIAL_MEDIA_CONFIG[0])}
              error={socialMediaErrors.facebook}
              placeholder="Enter username or link"
            />
            <FieldTextWithIconV1
              label="X"
              iconClassName={SOCIAL_MEDIA_CONFIG[1].iconClassName}
              value={person.twitter}
              onChange={(value) => dispatch(setPersonValue({ key: "twitter", value }))}
              onBlur={() => handleSocialMediaBlur(SOCIAL_MEDIA_CONFIG[1])}
              error={socialMediaErrors.twitter}
              placeholder="Enter username or link"
            />
          </div>
          <div className="tw-flex tw-gap-[24px]">
            <FieldTextWithIconV1
              label="LinkedIn"
              iconClassName={SOCIAL_MEDIA_CONFIG[2].iconClassName}
              value={person.linkedin}
              onChange={(value) => dispatch(setPersonValue({ key: "linkedin", value }))}
              onBlur={() => handleSocialMediaBlur(SOCIAL_MEDIA_CONFIG[2])}
              error={socialMediaErrors.linkedin}
              placeholder="Enter username or link"
            />
            <FieldTextWithIconV1
              label="Instagram"
              iconClassName={SOCIAL_MEDIA_CONFIG[3].iconClassName}
              value={person.instagram}
              onChange={(value) => dispatch(setPersonValue({ key: "instagram", value }))}
              onBlur={() => handleSocialMediaBlur(SOCIAL_MEDIA_CONFIG[3])}
              error={socialMediaErrors.instagram}
              placeholder="Enter username or link"
            />
          </div>
          <div className="tw-flex tw-gap-[24px]">
            <FieldTextWithIconV1
              label="YouTube"
              value={person.youtube}
              onChange={(value) => dispatch(setPersonValue({ key: "youtube", value }))}
              onBlur={() => handleSocialMediaBlur(SOCIAL_MEDIA_CONFIG[4])}
              error={socialMediaErrors.youtube}
              iconClassName={SOCIAL_MEDIA_CONFIG[4].iconClassName}
              placeholder="Enter username or link"
            />
            <FieldTextWithIconV1
              label="Pinterest"
              value={person.pinterest}
              onChange={(value) => dispatch(setPersonValue({ key: "pinterest", value }))}
              onBlur={() => handleSocialMediaBlur(SOCIAL_MEDIA_CONFIG[5])}
              error={socialMediaErrors.pinterest}
              iconClassName={SOCIAL_MEDIA_CONFIG[5].iconClassName}
              placeholder="Enter username or link"
            />
          </div>
          <div className="tw-flex tw-gap-[24px]">
            <FieldTextWithIconV1
              label="TikTok"
              value={person.tiktok}
              onChange={(value) => dispatch(setPersonValue({ key: "tiktok", value }))}
              onBlur={() => handleSocialMediaBlur(SOCIAL_MEDIA_CONFIG[6])}
              error={socialMediaErrors.tiktok}
              iconClassName={SOCIAL_MEDIA_CONFIG[6].iconClassName}
              placeholder="Enter username or link"
            />

            <div className="tw-flex-1" />
          </div>
        </>
      )}
    </div>
  );
};

PersonalDetailsView.propTypes = {
  birthdateError: PropTypes.string,
  modalType: PropTypes.string.isRequired,
};

PersonalDetailsView.defaultProps = {
  birthdateError: "",
};

export default PersonalDetailsView;
